<template>
  <div class="egg-shop-container">
    <!-- ==================== 游戏准备状态 (status === 0) ==================== -->
    <div v-if="status === 0" class="status-view">
      <!-- 背景图占位：请将 src 替换为您的背景图地址，或在此处使用 style="background-image: url('...')" -->
      <img src="@/assets/images/goldenEgg/bg_ready.jpg" class="status-bg-img" alt="背景图">

      <!-- 文字内容层 -->
      <div class="status-content">
        <i class="fas fa-hourglass-half status-icon"></i>
        <h2 class="status-title">游戏即将开始</h2>
        <p class="status-text">金蛋正在准备中，敬请期待...</p>
      </div>
    </div>

    <!-- ==================== 游戏进行状态 (status === 1) ==================== -->
    <template v-else-if="status === 1">
      <!-- 标题 -->
      <div class="header">
        <h1 class="title">金蛋选购</h1>
        <p class="subtitle">选择你心仪的金蛋</p>
      </div>

      <!-- 滚动区域 -->
      <div class="scroll-wrapper">
        <div class="eggs-grid">
          <div
              v-for="egg in eggs"
              :key="egg.id"
              class="egg-cell"
              :class="{
                selected: egg.isSelected,
                'egg-smashed': egg.status === 1,
                'egg-locked': egg.status === 2 && egg.lockedByMe === false
              }"
              @click="toggleEgg(egg)"
          >
            <div class="check-mark" v-if="egg.isSelected">
              <i class="fas fa-check"></i>
            </div>
            <div class="golden-egg">
              <div class="egg-highlight"></div>
              <div class="egg-number"><span>{{ egg.displayNumber }}</span></div>
            </div>
            <!-- 已被他人锁定 / 已砸的蛋：展示锁定/已开盖标记，不可再选 -->
            <div class="egg-lock-icon" v-if="egg.status === 2 && egg.lockedByMe === false">
              <i class="fas fa-lock"></i>
            </div>
            <div class="egg-lock-icon" v-else-if="egg.status === 1">
              <i class="fas fa-egg-broken"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部栏 -->
      <div class="bottom-bar">
        <div class="action-row">
          <button @click="selectAll" class="action-btn"><i class="fas fa-check-double"></i>全选</button>
          <button @click="clearAll" class="action-btn"><i class="fas fa-times"></i>清空</button>
          <button @click="randomSelect" class="action-btn"><i class="fas fa-dice"></i>随机</button>
          <div class="count-info"><i class="fas fa-shopping-cart"></i>{{ selectedCount }} 件</div>
        </div>

        <!-- 购买行 -->
        <div class="purchase-row">
          <!-- 左侧动态区域：购买前是预览列表，购买后展示已购买状态（中奖结果由统一推送处理） -->
          <div class="left-area">
            <!-- 情况A: 未购买 - 预览列表 -->
            <div v-if="!isPurchased" class="selected-preview">
              <transition-group name="badge" tag="div" class="preview-list">
                <span v-for="egg in selectedEggs" :key="egg.id" class="preview-item">{{ egg.displayNumber }}</span>
              </transition-group>
            </div>

            <!-- 情况B: 已购买 - 已购买状态 -->
            <div v-else class="purchased-box">
              <i class="fas fa-check-circle purchased-icon"></i>
              <span class="purchased-text">已购买</span>
            </div>
          </div>

          <!-- 右侧固定按钮 -->
          <button
              class="purchase-btn"
              :class="{ 'purchased': isPurchased, 'disabled': selectedCount === 0 && !isPurchased }"
              :disabled="selectedCount === 0 && !isPurchased"
              @click="purchase"
          >
            <i class="fas fa-bolt"></i>
            {{ isPurchased ? '已购买' : '立即购买' }}
          </button>
        </div>
      </div>
    </template>

    <!-- ==================== 游戏结束状态 (status === 2) ==================== -->
    <div v-else-if="status === 2" class="status-view end-view">
      <!-- 背景图占位：结束页面如果也需要背景图，可在此替换 -->
      <div class="status-content">
        <i class="fas fa-flag-checkered status-icon"></i>
        <h2 class="status-title">游戏已结束</h2>
        <p class="status-text">感谢参与，中奖结果请留意通知</p>
      </div>
    </div>

    <!-- 粒子容器 -->
    <div class="particles" ref="particles"></div>

    <!-- ==================== ✅ 购买金蛋弹窗（未支付时强制弹出，不可点遮罩关闭） ==================== -->
    <transition name="modal">
      <div v-if="payModalVisible" class="pay-modal">
        <div class="pay-modal-box" @click.stop>
          <div class="modal-header">
            <h3>🥚 购买金蛋</h3>
          </div>
          <div class="pay-modal-body">
            <p class="pay-tip">参与砸金蛋需先购买金蛋，支付成功后即可开始游戏</p>

            <!-- 数量步进器：默认 3 个，可加减 -->
            <div class="stepper-row">
              <span class="stepper-label">购买数量</span>
              <div class="stepper">
                <button class="stepper-btn" :class="{ disabled: eggCount <= minEggCount }" @click="decreaseCount">−</button>
                <span class="stepper-num">{{ eggCount }}</span>
                <button class="stepper-btn" :class="{ disabled: eggCount >= maxEggCount }" @click="increaseCount">＋</button>
              </div>
            </div>

            <!-- 价格 -->
            <div class="price-row">
              <span>单价 ¥{{ eggUnitPrice }} / 个</span>
              <span class="total-price">合计<em>¥{{ totalPrice }}</em></span>
            </div>

            <button class="pay-confirm-btn" :disabled="submittingOrder" @click="submitOrder">
              <i class="fas fa-bolt"></i>
              {{ submittingOrder ? '下单中...' : '立即购买' }}
            </button>

            <!-- 支付完返回但状态没刷出来时的兜底入口 -->
            <p class="pay-requery" @click="requeryPayStatus">已完成支付？点击查询支付结果</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast 提示 -->
    <transition name="toast">
      <div v-if="showToast" class="toast"><i class="fas fa-info-circle"></i>{{ toastMessage }}</div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {_code} from "@utils/constant";
import {Toast} from "vant";
import i18nMixin from "@/libs/i18nMixin";
export default {
  name: 'GoldenEggShop',
  mixins: [i18nMixin],
  data() {
    return {
      selectedCount: 0,
      showToast: false,
      toastMessage: '',
      eggs: [],
      isPurchased: false,

      // ===== ✅ 支付购买相关 =====
      hasPaid: false,          // checkIsPay 结果：是否已支付
      checkingPay: false,      // 支付状态查询中（防重复请求）
      payModalVisible: false,  // 购买窗口显隐
      eggCount: 3,             // 购买数量，默认 3
      minEggCount: 1,          // 最少购买数量
      maxEggCount: 10,         // ✅ TODO: 单次购买上限，按业务调整
      eggUnitPrice: 1,         // ✅ TODO: 金蛋单价（元），如有配置接口请替换
      submittingOrder: false   // 下单中
    }
  },
  computed: {
    ...mapGetters(["live","liveGame"]),
    // 0=准备 1=进行 2=结束
    status() {
      return this.liveGame ? this.liveGame.status : 0;
    },
    selectedEggs() { return this.eggs.filter(egg => egg.isSelected) },
    selectedEggIds() { return this.selectedEggs.map(item => item.id) },

    // ✅ 期次ID：checkResult / createOrder 都需要。
    // TODO: 目前优先取路由参数 drawId，兜底取 liveGame 的 drawId / id，请按实际数据结构确认
    drawId() {
      return this.$route.query.drawingId || this.live.drawingId || '';
    },
    // ✅ 合计金额
    totalPrice() {
      return (this.eggCount * Number(this.eggUnitPrice || 0)).toFixed(2);
    }
  },
  watch: {
    status(newVal) {
      // ✅ 游戏开始 → 查询支付状态，未支付自动弹购买窗口；并刷新蛋状态
      if (newVal === 1) {
        this.checkPayStatus();
        this.refreshEggs();
      } else {
        this.payModalVisible = false; // ✅ 非进行中一律收起购买窗口
      }
    }
  },
  created() { this.initEggs() },
  mounted() {
    if (this.$route.query.liveGameUserId) {
      this.$store.commit(
          "liveGame/SET_LIVE_GAME_USER_ID",
          this.$route.query.liveGameUserId
      );
    }
    if(this.$route.query.liveGameId){
      const loadingToast = Toast.loading({
        duration: 0,
        forbidClick: true,
        message: this.$i18n("queryStatus", "查询状态..."),
      });
      this.$store.dispatch("liveGame/getLiveGame",this.$route.query.liveGameId).then(res=>{
        const status = res.status;
        // ✅ 进行中状态主动查一次支付状态（兜底，防止 watch 未触发）+ 刷新蛋状态
        if(status === 1){
          this.checkPayStatus();
          this.refreshEggs();
        }
      }).finally(() => {
        loadingToast.clear();
      });
    }
    // 1. 定义处理函数，绑定 this
    this.handleSocketMessage = this.handleSocketMessage.bind(this);
    this.$store.commit('ADD_MESSAGE_LISTENER', this.handleSocketMessage);

    // ✅ 从小程序支付页返回（webview 重新可见）时，自动复查支付结果
    this._onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && this.status === 1 && !this.hasPaid) {
        this.checkPayStatus(true); // silent：不重复弹 toast
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  },
  beforeDestroy() {
    // ✅ 移除支付结果复查监听
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }
    this.$store.commit('REMOVE_MESSAGE_LISTENER', this.handleSocketMessage);
  },
  methods: {
    handleSocketMessage(msg){
      switch (msg.msgType) {
        case _code.INIT_EGG: // 初始化抢单 / 重置游戏
          console.log('收到 INIT_EGG 消息，强制重置游戏状态');
          // 1. 强制重置购买状态（核心需求：不管是否购买，都重新来过）
          this.isPurchased = false;
          // 2. 重置选中计数
          this.selectedCount = 0;
          // 3. 重置金蛋列表
          this.initEggs();
          // 4. 提示用户
          this.showToastMessage('新一轮金蛋已准备，请重新选择！');
          // ✅ 新一轮开始：重新确认支付状态（后端判定未支付会自动弹购买窗口）
          this.checkPayStatus();
          break;
        case _code.INIT_EGG: // 抢金蛋通知
          // 如果有其他逻辑保留即可
          break;
      }
    },
    async initEggs() {
      // 蛋数以本场实际为准（后端固定 TOTAL_EGGS=36，listEggs 有几个返回几个）
      let total = 36
      try {
        if (this.drawId) {
          const res = await this.$http.get('/api/goldenEgg/listEggs', { params: { drawId: this.drawId } })
          const list = res.data || []
          if (list.length) {
            this.eggs = list.map(item => ({
              id: item.eggIdx,
              displayNumber: item.eggIdx < 10 ? '0' + item.eggIdx : String(item.eggIdx),
              isSelected: false,
              status: item.status,
              lockedByMe: item.lockedByMe
            }))
            return
          }
        }
      } catch (e) {
        console.error('初始化蛋列表失败', e)
      }
      // 兜底：按后端 TOTAL_EGGS=36 生成
      this.eggs = []
      for (let i = 1; i <= total; i++) {
        const displayNumber = i < 10 ? '0' + i : String(i)
        this.eggs.push({ id: i, displayNumber: displayNumber, isSelected: false, status: 0, lockedByMe: null })
      }
    },

    // ✅ 刷新蛋状态：调 listEggs（后端返回哪些蛋被锁定 / 可抢），有几个返回几个，叠加到本地蛋上
    async refreshEggs() {
      if (!this.drawId) return
      try {
        const res = await this.$http.get('/api/goldenEgg/listEggs', { params: { drawId: this.drawId } })
        const list = res.data || []
        const map = {}
        list.forEach(item => { map[item.eggIdx] = item })
        this.eggs.forEach(egg => {
          const st = map[egg.id]
          if (st) {
            egg.status = st.status
            egg.lockedByMe = st.lockedByMe
          }
        })
      } catch (e) {
        console.error('刷新蛋状态失败', e)
      }
    },

    // ================================================================
    // ✅ 支付购买流程
    // ================================================================

    // 查询是否已支付（后端 checkIsPay）
    // silent=true 时不弹 toast / 不弹窗（用于支付返回后的静默复查）
    checkPayStatus(silent) {
      if (this.checkingPay) return Promise.resolve();
      if (!this.drawId) {
        console.warn('[GoldenEgg] 缺少 drawId，无法查询支付状态');
        return Promise.resolve();
      }
      this.checkingPay = true;
      return this.$http.get('/api/goldenEgg/checkIsPay', { params: { drawId: this.drawId } })
          .then(res => {
            const paid = res.data === true || res.data === 'true';
            this.hasPaid = paid;
            if (paid) {
              this.payModalVisible = false;
              if (silent) this.showToastMessage('支付成功，开始游戏！');
            } else if (!silent) {
              // 未购买 → 弹出购买窗口
              this.payModalVisible = true;
            }
          })
          .catch(err => {
            console.error('查询支付状态失败', err);
            if (!silent) {
              this.showToastMessage('支付状态查询失败，请重试');
              this.payModalVisible = true; // 弹窗内有"重新查询"入口兜底
            }
          })
          .finally(() => { this.checkingPay = false; });
    },

    // 数量加减
    increaseCount() {
      if (this.eggCount >= this.maxEggCount) {
        return this.showToastMessage(`最多可购买 ${this.maxEggCount} 个`);
      }
      this.eggCount++;
      if (navigator.vibrate) navigator.vibrate(20);
    },
    decreaseCount() {
      if (this.eggCount <= this.minEggCount) return;
      this.eggCount--;
      if (navigator.vibrate) navigator.vibrate(20);
    },

    // 下单 → 跳转小程序收银台
    submitOrder() {
      if (this.submittingOrder) return;
      if (!this.drawId) return this.showToastMessage('未获取到期次信息，无法下单');
      this.submittingOrder = true;

      this.$http.post('/api/goldenEgg/createOrder', {
        drawId: this.drawId,
        liveId: this.live ? this.live.id : undefined,
        eggNum: this.eggCount,
        payType: 'pay_golden_egg'
        // ⚠️ 不传 price：建议后端按 eggNum × 单价 计算，防止客户端篡改金额
      }).then(res => {
        const order = res.data || {};
        // ✅ TODO: uni = 支付单号，请确认后端订单里取 orderId 还是 id
        const uni = order.orderId || order.id;
        if (!uni) {
          this.showToastMessage('下单失败');
          return;
        }
        this.showToastMessage('正在跳转支付...');
        this.goPay(uni);
      }).catch(err => {
        console.error('下单失败', err);
        this.showToastMessage((err && err.message) || '下单失败');
      }).finally(() => {
        this.submittingOrder = false;
      });
    },

    // 跳小程序支付页
    goPay(uni) {
      if (window.wx && window.wx.miniProgram) {
        window.wx.miniProgram.navigateTo({
          url: `/pages/wxpay/wxpay?uni=${uni}&payType=pay_golden_egg`
        });
      } else {
        this.showToastMessage('请在微信小程序内打开完成支付');
      }
    },

    // 弹窗内手动复查支付结果（支付完自动刷新失败时的兜底）
    requeryPayStatus() {
      if (this.checkingPay) return;
      const loading = Toast.loading({ duration: 0, forbidClick: true, message: '查询支付结果...' });
      this.checkPayStatus(true).finally(() => loading.clear());
    },

    // ✅ 未支付统一拦截：toast + 弹出购买窗口
    ensurePaid() {
      if (this.hasPaid) return true;
      this.showToastMessage('请先购买金蛋');
      this.payModalVisible = true;
      return false;
    },

    // ================================================================

    // ✅ 该蛋当前是否还能被选中（未砸、未被他人锁定）
    canSelect(egg) {
      return !(egg.status === 1 || (egg.status === 2 && egg.lockedByMe === false))
    },

    async toggleEgg(egg) {
      if (navigator.vibrate) navigator.vibrate(30)
      if (this.isPurchased) return; // 购买后锁定
      if (!this.ensurePaid()) return; // ✅ 未支付拦截

      // 反选：本地取消选中，并调 unlockEgg 释放后台锁定，供他人再抢
      if (egg.isSelected) {
        egg.isSelected = false
        this.selectedCount--
        this.playDeselectSound()
        this.unlockEgg(egg.id)
        return
      }

      // 已被砸 / 已被他人锁定：不可选
      if (!this.canSelect(egg)) {
        this.showToastMessage('手慢了，该蛋已被抢/锁定')
        this.refreshEggs()
        return
      }

      // 点的时候：通过 lockEgg 接口尝试锁定，返回是否能被锁定
      try {
        await this.$http.post('/api/goldenEgg/lockEgg', null, {
          params: { drawId: this.drawId, eggIdx: egg.id }
        })
        // 锁定成功 → 选中
        egg.isSelected = true
        this.selectedCount++
        this.playSelectSound()
        this.createParticles(egg.id)
      } catch (e) {
        console.error('锁定失败', e)
        this.showToastMessage((e && e.message) || '手慢了，该蛋已被抢')
      }
      // 点的时候：刷新 listEggs 蛋状态，看哪些还能被锁定
      this.refreshEggs()
    },

    // ✅ 反选即解锁：释放后台对该蛋的锁定（幂等，未锁定直接成功）
    async unlockEgg(eggIdx) {
      if (!this.drawId) return
      try {
        await this.$http.post('/api/goldenEgg/unlockEgg', null, {
          params: { drawId: this.drawId, eggIdx: eggIdx }
        })
      } catch (e) {
        console.error('解锁失败', e)
      }
      this.refreshEggs()
    },

    selectAll() {
      if (this.isPurchased) return;
      if (!this.ensurePaid()) return; // ✅ 未支付拦截
      this.eggs.forEach(egg => { if (!egg.isSelected && this.canSelect(egg)) { egg.isSelected = true; this.selectedCount++ } }); this.showToastMessage('已全选')
    },
    clearAll() {
      if (this.isPurchased) return;
      this.eggs.forEach(egg => { egg.isSelected = false }); this.selectedCount = 0; this.showToastMessage('已清空')
    },
    randomSelect() {
      if (this.isPurchased) return;
      if (!this.ensurePaid()) return; // ✅ 未支付拦截
      this.clearAll()
      const count = Math.floor(Math.random() * 10) + 5
      const avail = this.eggs.filter(egg => this.canSelect(egg))
      const shuffled = [...avail].sort(() => Math.random() - 0.5)
      for (let i = 0; i < count && i < shuffled.length; i++) { shuffled[i].isSelected = true; this.selectedCount++ }
      this.showToastMessage(`随机选了 ${Math.min(count, shuffled.length)} 个`)
    },

    purchase() {
      if (this.isPurchased) return;
      if (!this.ensurePaid()) return; // ✅ 未支付拦截（正常流程选不了蛋，这里双保险）
      if (this.selectedCount === 0) { this.showToastMessage('请先选择金蛋'); return }
      if (navigator.vibrate) navigator.vibrate([50, 30, 50, 30, 100])

      this.playPurchaseSound()
      this.createPurchaseParticles()

      const params = { liveId: this.live.id, eggs: this.selectedEggIds }
      this.$http.post("/api/goldenEgg/buyEgg", params).then(res => {
        this.isPurchased = true;
        this.eggs.forEach(egg => egg.isSelected = false);
        this.selectedCount = 0;
        this.showToastMessage('购买成功！')
      }).catch(err => { console.error(err); this.showToastMessage('购买失败') })
    },

    showToastMessage(message) { this.toastMessage = message; this.showToast = true; setTimeout(() => { this.showToast = false }, 2000) },

    createParticles(eggId) {
      const eggElement = document.querySelectorAll('.egg-cell')[eggId - 1]
      if (!eggElement) return
      const rect = eggElement.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const colors = ['#FFD700', '#FFA500', '#FFFAF0']
      const container = this.$refs.particles

      for (let i = 0; i < 8; i++) {
        const particle = document.createElement('div')
        particle.classList.add('particle')
        particle.style.position = 'absolute'
        particle.style.left = centerX + 'px'
        particle.style.top = centerY + 'px'
        particle.style.background = colors[Math.floor(Math.random() * colors.length)]
        const angle = (Math.PI * 2 * i) / 8
        const velocity = 30 + Math.random() * 30
        particle.style.setProperty('--tx', Math.cos(angle) * velocity + 'px')
        particle.style.setProperty('--ty', Math.sin(angle) * velocity + 'px')
        particle.style.animation = 'particleFly 0.6s ease-out forwards'
        container.appendChild(particle)
        setTimeout(() => { if (particle.parentNode) particle.parentNode.removeChild(particle) }, 600)
      }
    },
    createPurchaseParticles() {
      const container = this.$refs.particles
      const colors = ['#FFD700', '#FFA500', '#FFFAF0']
      const vw = window.innerWidth || 375
      const vh = window.innerHeight || 667
      const startX = vw / 2
      const startY = vh * 0.8

      for (let i = 0; i < 50; i++) {
        const particle = document.createElement('div')
        particle.classList.add('particle')
        particle.style.position = 'absolute'
        particle.style.left = startX + 'px'
        particle.style.top = startY + 'px'
        particle.style.background = colors[Math.floor(Math.random() * colors.length)]
        particle.style.width = '8px'
        particle.style.height = '8px'
        const angle = Math.random() * Math.PI * 2
        const velocity = 100 + Math.random() * 150
        particle.style.setProperty('--tx', Math.cos(angle) * velocity + 'px')
        particle.style.setProperty('--ty', Math.sin(angle) * velocity - 100 + 'px')
        particle.style.animation = 'particleFly 1.5s ease-out forwards'
        container.appendChild(particle)
        setTimeout(() => { if (particle.parentNode) particle.parentNode.removeChild(particle) }, 1500)
      }
    },

    playSelectSound() { try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); const o = ctx.createOscillator(); const g = ctx.createGain(); o.connect(g); g.connect(ctx.destination); o.frequency.setValueAtTime(800, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1); g.gain.setValueAtTime(0.2, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2); o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.2) } catch (e) {} },
    playDeselectSound() { try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); const o = ctx.createOscillator(); const g = ctx.createGain(); o.connect(g); g.connect(ctx.destination); o.frequency.setValueAtTime(600, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1); g.gain.setValueAtTime(0.15, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2); o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.2) } catch (e) {} },
    playPurchaseSound() { try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); [523, 659, 784].forEach((f, i) => { setTimeout(() => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.connect(g); g.connect(ctx.destination); o.frequency.setValueAtTime(f, ctx.currentTime); g.gain.setValueAtTime(0.2, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3); o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.3) }, i * 150) }) } catch (e) {} }
  }
}
</script>

<style scoped>
* { -webkit-tap-highlight-color: transparent; user-select: none; box-sizing: border-box; }

.egg-shop-container {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  display: flex; flex-direction: column; overflow: hidden;
  font-family: 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  color: white;
  background-color: #0a0817;
}

/* === 准备/结束状态页样式 === */
.status-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  z-index: 1;
  overflow: hidden;
}

/* 背景图样式 */
.status-bg-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
}

/* 文字内容层，确保在图片之上 */
.status-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 400px;
}

.status-icon {
  font-size: 60px;
  color: #FFD700;
  margin-bottom: 20px;
  filter: drop-shadow(0 0 15px rgba(0, 0, 0, 0.8));
  animation: bellShake 2s infinite ease-in-out;
}
.status-title {
  font-size: 26px;
  font-weight: bold;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  margin-bottom: 10px;
  text-shadow: 0 2px 10px rgba(0,0,0,0.6);
}
.status-text {
  font-size: 15px;
  color: rgba(255,255,255,0.9);
  margin-bottom: 30px;
  text-shadow: 0 1px 5px rgba(0,0,0,0.8);
}

/* 原有游戏内样式 */
.header { flex-shrink: 0; padding: 15px 16px 10px; text-align: center; position: relative; z-index: 1;
  background: radial-gradient(circle at 50% -10%, rgba(255, 215, 0, 0.2) 0%, transparent 50%), linear-gradient(180deg, #181235 0%, transparent 100%);
}
.title { font-size: 20px; font-weight: 700; margin-bottom: 4px; background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.subtitle { color: #9CA3AF; font-size: 12px; }

.scroll-wrapper { flex: 1; overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch; overscroll-behavior-y: contain; position: relative; z-index: 1;
  background: linear-gradient(180deg, #0a0817 0%, #1a1235 50%, #0a0817 100%);
}
.eggs-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; padding: 12px 10px; }
.egg-cell { position: relative; display: flex; align-items: center; justify-content: center; padding: 6px 4px; border-radius: 10px; cursor: pointer; background: rgba(255,255,255,0.03); border: 2px solid transparent; transition: all 0.2s; }
.egg-cell.selected { background: rgba(255,215,0,0.15); border-color: rgba(255,215,0,0.6); transform: scale(1.05); box-shadow: 0 0 20px rgba(255,215,0,0.4); }
.egg-cell:active { transform: scale(0.95); }
.egg-cell.egg-locked { cursor: not-allowed; opacity: 0.55; }
.egg-cell.egg-locked .golden-egg { filter: grayscale(0.7); }
.egg-cell.egg-smashed { cursor: not-allowed; opacity: 0.45; }
.egg-cell.egg-smashed .golden-egg { filter: grayscale(1) brightness(0.8); }
.egg-lock-icon { position: absolute; top: 4px; left: 4px; width: 18px; height: 18px; background: rgba(0,0,0,0.55); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #FF4D4F; z-index: 9; }
.check-mark { position: absolute; top: 2px; right: 2px; width: 20px; height: 20px; background: linear-gradient(135deg, #FFD700, #FFA500); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #000; z-index: 10; box-shadow: 0 2px 8px rgba(255,215,0,0.6); animation: badgePop 0.3s ease-out; }
@keyframes badgePop { 0% { transform: scale(0); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }
.golden-egg { position: relative; width: 48px; height: 60px; border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%; background: radial-gradient(ellipse at 35% 30%, #FFFAF0 0%, #FFD700 15%, #DAA520 50%, #B8860B 70%, #5D4E0A 100%); box-shadow: inset -8px -12px 20px rgba(0,0,0,0.4), inset 6px 8px 15px rgba(255,255,255,0.3), 0 5px 15px rgba(0,0,0,0.5); transition: transform 0.2s; }
.egg-cell.selected .golden-egg { transform: translateY(-2px); filter: brightness(1.2); }
.egg-highlight { position: absolute; top: 15%; left: 25%; width: 30%; height: 20%; background: radial-gradient(ellipse, rgba(255,255,255,0.8) 0%, transparent 70%); border-radius: 50%; filter: blur(3px); transform: rotate(-20deg); }
.egg-number { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 5; }
.egg-number span { font-size: 13px; font-weight: 800; color: rgba(255,255,255,0.95); text-shadow: 0 0 8px rgba(0,0,0,0.8); }

.bottom-bar { flex-shrink: 0; width: 100%; background: rgba(15, 10, 25, 0.9); backdrop-filter: blur(20px); border-top: 1px solid rgba(255,215,0,0.2); z-index: 100; display: flex; flex-direction: column; position: relative; }
.action-row { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-bottom: 1px solid rgba(255,215,0,0.1); flex-shrink: 0; }
.action-btn { padding: 6px 12px; background: rgba(255,215,0,0.1); border: 1px solid rgba(255,215,0,0.3); color: #FFD700; border-radius: 16px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px; }
.action-btn:active { transform: scale(0.95); background: rgba(255,215,0,0.2); }
.count-info { margin-left: auto; display: flex; align-items: center; gap: 5px; font-size: 13px; color: white; }
.count-info i { color: #FFD700; }

.purchase-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px; height: 65px; }

.left-area { flex: 1; height: 100%; position: relative; }
.selected-preview { height: 100%; background: rgba(255,255,255,0.05); border-radius: 10px; padding: 4px 8px; overflow: hidden; display: flex; align-items: center; }
.preview-list { display: flex; flex-wrap: wrap; gap: 4px; align-content: flex-start; }
.preview-item { width: 26px; height: 20px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #FFD700, #FFA500); color: #000; font-size: 11px; font-weight: 700; border-radius: 4px; flex-shrink: 0; text-align: center; white-space: nowrap; }

.purchased-box { width: 100%; height: 100%; background: rgba(255,255,255,0.05); border-radius: 10px; display: flex; flex-direction: column; justify-content: center; align-items: center; }
.purchased-icon { font-size: 22px; color: #FFD700; margin-bottom: 4px; }
.purchased-text { color: #FFD700; font-size: 13px; font-weight: bold; letter-spacing: 1px; }
@keyframes bellShake { 0%, 100% { transform: rotate(0); } 10% { transform: rotate(15deg); } 20% { transform: rotate(-15deg); } 30% { transform: rotate(10deg); } 40% { transform: rotate(-10deg); } 50% { transform: rotate(0deg); } }

.purchase-btn { width: 110px; height: 44px; padding: 0 15px; background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%); color: #000; font-weight: 700; border-radius: 22px; border: none; cursor: pointer; font-size: 14px; white-space: nowrap; box-shadow: 0 4px 15px rgba(255,215,0,0.4); display: flex; align-items: center; justify-content: center; gap: 5px; transition: all 0.3s; }
.purchase-btn:active { transform: scale(0.95); }
.purchase-btn.disabled { background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.3); box-shadow: none; cursor: not-allowed; }
.purchase-btn.purchased { background: #555; color: #fff; box-shadow: none; cursor: default; }

.modal-header { padding: 15px; text-align: center; border-bottom: 1px solid rgba(255,255,255,0.1); position: relative; }
.modal-header h3 { color: #FFD700; margin: 0; font-size: 18px; }

.particles { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 50; overflow: hidden; }
.particle { position: absolute; width: 4px; height: 4px; border-radius: 50%; pointer-events: none; transform: translate(-50%, -50%); opacity: 0; }
@keyframes particleFly { 0% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(0); opacity: 0; } }

.toast { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.8); backdrop-filter: blur(10px); color: white; padding: 12px 24px; border-radius: 20px; font-size: 14px; z-index: 999; display: flex; align-items: center; gap: 8px; border: 1px solid rgba(255,215,0,0.3); }
.toast i { color: #FFD700; }

.badge-enter-active, .badge-leave-active { transition: all 0.3s; }
.badge-enter, .badge-leave-to { opacity: 0; transform: scale(0); }
.modal-enter-active, .modal-leave-active { transition: opacity 0.3s; }
.modal-enter, .modal-leave-to { opacity: 0; }
.toast-enter-active, .toast-leave-active { transition: all 0.3s; }
.toast-enter, .toast-leave-to { opacity: 0; transform: translate(-50%, -60%); }

/* ==================== ✅ 购买金蛋弹窗 ==================== */
.pay-modal { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.85); z-index: 1100; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(8px); }
.pay-modal-box { width: 320px; max-width: 88vw; background: #15102e; border-radius: 16px; border: 1px solid rgba(255,215,0,0.35); overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.6); animation: payPop 0.25s ease-out; }
@keyframes payPop { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.pay-modal-body { padding: 20px 20px 22px; }
.pay-tip { font-size: 13px; color: #9CA3AF; text-align: center; margin: 0 0 18px; line-height: 1.6; }
.stepper-row { display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,215,0,0.15); border-radius: 12px; padding: 12px 16px; margin-bottom: 14px; }
.stepper-label { font-size: 14px; color: #fff; }
.stepper { display: flex; align-items: center; gap: 12px; }
.stepper-btn { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg, #FFD700, #FFA500); color: #000; font-size: 20px; font-weight: bold; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; line-height: 1; padding: 0; }
.stepper-btn:active { transform: scale(0.92); }
.stepper-btn.disabled { background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.3); }
.stepper-num { min-width: 40px; text-align: center; font-size: 20px; font-weight: 800; color: #FFD700; }
.price-row { display: flex; justify-content: space-between; align-items: baseline; padding: 0 4px; margin-bottom: 16px; font-size: 13px; color: #9CA3AF; }
.total-price em { font-style: normal; font-size: 22px; font-weight: 800; color: #FFD700; margin-left: 4px; }
.pay-confirm-btn { width: 100%; height: 46px; background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%); color: #000; font-weight: 700; font-size: 15px; border-radius: 23px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; box-shadow: 0 4px 15px rgba(255,215,0,0.4); }
.pay-confirm-btn:active { transform: scale(0.97); }
.pay-confirm-btn:disabled { opacity: 0.6; }
.pay-requery { margin: 14px 0 0; text-align: center; font-size: 12px; color: #9CA3AF; text-decoration: underline; cursor: pointer; }

@media (max-width: 320px) { .golden-egg { width: 42px; height: 52px; } .preview-item { width: 24px; } .purchase-btn { width: 100px; } }
@media (min-width: 414px) { .golden-egg { width: 52px; height: 64px; } .preview-item { width: 28px; } }
</style>
